import React, { useState } from 'react';
import { Button, Modal, List, Empty, Popconfirm, Progress, Tag } from 'antd';
import { HistoryOutlined, DeleteOutlined, LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../store/store';
import { clearHistory } from '../store/uploadHistorySlice';

export const HistoryButton: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const uploads = useSelector((state: RootState) => state.uploadHistory.uploads);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  if (!isAuthenticated) {
    return null;
  }

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const handleHistoryClick = async (item: any) => {
    if (item.success && item.status === 'completed') {
      // Completed upload - navigate to results
      navigate(`/upload/${item.flowId}`);
      setModalVisible(false);
    } else if (item.status === 'processing') {
      // Still processing - navigate to polling status page
      navigate(`/polling/${item.flowId}`);
      setModalVisible(false);
    }
  };

  const handleClearHistory = () => {
    dispatch(clearHistory());
  };

  return (
    <>
      <Button 
        icon={<HistoryOutlined />}
        onClick={() => setModalVisible(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        History
      </Button>
      
      <Modal
        title="Upload History"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={
          uploads.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Popconfirm
                title="Clear History"
                description="Are you sure you want to delete all upload history? This action cannot be undone."
                onConfirm={handleClearHistory}
                okText="Yes, Clear All"
                cancelText="Cancel"
                okType="danger"
              >
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                >
                  Clear History
                </Button>
              </Popconfirm>
              <Button onClick={() => setModalVisible(false)}>
                Close
              </Button>
            </div>
          ) : null
        }
        width={600}
        styles={{
          body: { 
            padding: uploads.length === 0 ? '40px 24px' : '16px 16px 16px 0',
            maxHeight: '60vh',
            overflowY: 'auto'
          }
        }}
      >
        {uploads.length === 0 ? (
          <Empty
            description="No uploads yet"
            style={{ margin: '20px 0' }}
          />
        ) : (
          <List
            dataSource={uploads}
            renderItem={(item) => {
              const status = item.status;
              const isCompleted = status === 'completed';
              const isProcessing = status === 'processing';
              
              let backgroundColor: string;
              let borderColor: string; 
              let textColor: string;
              let statusIcon: React.ReactNode;
              let statusText: string;
              
              if (isCompleted) {
                backgroundColor = '#f6ffed';
                borderColor = '#b7eb8f';
                textColor = '#000000d9';
                statusIcon = <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                statusText = 'Completed';
              } else if (isProcessing) {
                backgroundColor = '#fff7e6';
                borderColor = '#ffd591';
                textColor = '#000000d9';
                statusIcon = <LoadingOutlined style={{ color: '#fa8c16' }} />;
                statusText = 'Processing';
              } else {
                // Failed or unknown status
                backgroundColor = '#fff2f0';
                borderColor = '#ffccc7';
                textColor = '#00000073';
                statusIcon = <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
                statusText = 'Failed';
              }
              
              const canClick = isCompleted || isProcessing;
              
              return (
                <List.Item
                  style={{ 
                    cursor: canClick ? 'pointer' : 'not-allowed',
                    padding: '12px 16px',
                    borderRadius: 6,
                    margin: '4px 8px',
                    transition: 'all 0.2s',
                    backgroundColor: backgroundColor,
                    border: `1px solid ${borderColor}`,
                    opacity: canClick ? 1 : 0.7
                  }}
                  onClick={() => handleHistoryClick(item)}
                  onMouseEnter={(e) => {
                    if (canClick) {
                      e.currentTarget.style.backgroundColor = '#f0f9ff';
                      e.currentTarget.style.borderColor = '#91d5ff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = backgroundColor;
                    e.currentTarget.style.borderColor = borderColor;
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* Status Tag - at the top left */}
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginBottom: 4
                    }}>
                      <Tag color={isCompleted ? 'success' : isProcessing ? 'processing' : 'error'}>
                        {statusText}
                      </Tag>
                    </div>
                    
                    {/* File name with status icon */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 4
                    }}>
                      <div style={{ marginRight: 8, flexShrink: 0 }}>
                        {statusIcon}
                      </div>
                      <div style={{ 
                        fontWeight: 500, 
                        fontSize: '14px',
                        color: textColor,
                      }}>
                        {item.fileName}
                      </div>
                    </div>
                    
                    {/* Progress bar for processing items */}
                    {isProcessing && item.completionPercentage !== undefined && (
                      <div style={{ marginBottom: 8 }}>
                        <Progress 
                          percent={Math.round(item.completionPercentage)} 
                          size="small"
                          showInfo={true}
                          format={(percent) => `${percent}% (${item.completedFiles}/${item.totalFiles})`}
                        />
                      </div>
                    )}
                    
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#8c8c8c'
                    }}>
                      {isCompleted 
                        ? `Completed on ${formatTimestamp(item.timestamp)}`
                        : isProcessing
                          ? `Started on ${formatTimestamp(item.timestamp)}`
                          : `Failed on ${formatTimestamp(item.timestamp)}`
                      }
                      {item.errorMessage && (
                        <div style={{ color: '#ff4d4f', marginTop: 4 }}>
                          {item.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </>
  );
};